import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { auth } from '../../firebase';
import {
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  TruckIcon
} from '@heroicons/react/24/outline';

const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDrivers = async () => {
      setLoading(true);
      setError('');
      try {
        // Placeholder request - replace with your actual API endpoint
        const token = await auth.currentUser?.getIdToken();
        // Simulate driver data with cars, appointments, vinchecks, credits, etc.
        const response = await axios.get('https://jsonplaceholder.typicode.com/users', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        // Add placeholder extra fields for demo
        const demoDrivers = (response.data || []).map((driver, idx) => ({
          ...driver,
          cars: [
            {
              make: 'Dacia',
              model: 'Logan',
              registration: 'CJ01ABC',
              itp: '2025-12-01',
              rca: '2025-06-01',
              casco: idx % 2 === 0 ? '2025-09-01' : null
            },
            {
              make: 'VW',
              model: 'Golf',
              registration: 'CJ02XYZ',
              itp: '2024-11-15',
              rca: '2024-07-10',
              casco: null
            }
          ],
          appointments: Math.floor(Math.random() * 10) + 1,
          vinchecks: Math.floor(Math.random() * 5),
          credits: Math.floor(Math.random() * 100)
        }));
        setDrivers(demoDrivers);
      } catch (err) {
        setError('Failed to fetch drivers.');
      } finally {
        setLoading(false);
      }
    };
    fetchDrivers();
  }, []);

  // Stats calculation
  const totalDrivers = drivers.length;
  const totalCars = drivers.reduce((sum, d) => sum + d.cars.length, 0);
  const totalAppointments = drivers.reduce((sum, d) => sum + d.appointments, 0);
  const totalVinChecks = drivers.reduce((sum, d) => sum + d.vinchecks, 0);
  const totalCredits = drivers.reduce((sum, d) => sum + d.credits, 0);
  const totalCreditValueRON = totalCredits; // 1 credit = 1 RON

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <svg className="animate-spin h-12 w-12 text-purple-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-4 text-lg text-gray-600">Loading drivers...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
      <div className="mx-auto px-4 py-6 sm:px-6 md:px-8">
      {/* Statistics Cards - horizontally scrollable on mobile */}
      <div className="mb-8 overflow-x-auto pb-2">
        <div className="flex gap-4 min-w-max md:grid md:grid-cols-3 lg:grid-cols-6 md:min-w-0">
        <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
          <div className="p-4 md:p-5 flex items-center">
            <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
              <UserIcon className="w-5 h-5 text-white" />
            </div>
            <div className="ml-4 md:ml-5">
              <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Drivers</dt>
              <dd className="text-base md:text-lg font-medium text-gray-900">{totalDrivers}</dd>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
          <div className="p-4 md:p-5 flex items-center">
            <TruckIcon className="w-8 h-8 bg-blue-500 rounded-md p-1 text-white" />
            <div className="ml-4 md:ml-5">
              <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Cars</dt>
              <dd className="text-base md:text-lg font-medium text-gray-900">{totalCars}</dd>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
          <div className="p-4 md:p-5 flex items-center">
            <svg className="w-8 h-8 bg-green-500 rounded-md p-1 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" /></svg>
            <div className="ml-4 md:ml-5">
              <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Appointments</dt>
              <dd className="text-base md:text-lg font-medium text-gray-900">{totalAppointments}</dd>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
          <div className="p-4 md:p-5 flex items-center">
            <svg className="w-8 h-8 bg-blue-500 rounded-md p-1 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <div className="ml-4 md:ml-5">
              <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">VIN Checks</dt>
              <dd className="text-base md:text-lg font-medium text-gray-900">{totalVinChecks}</dd>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
          <div className="p-4 md:p-5 flex items-center">
            <svg className="w-8 h-8 bg-green-600 rounded-md p-1 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" /></svg>
            <div className="ml-4 md:ml-5">
              <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Credits</dt>
              <dd className="text-base md:text-lg font-medium text-gray-900">{totalCredits}</dd>

            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg w-44 flex-shrink-0 md:w-auto">
          <div className="p-4 md:p-5 flex items-center">
            <svg className="w-8 h-8 bg-orange-500 rounded-md p-1 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
            <div className="ml-4 md:ml-5">
              <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Credit Value (RON)</dt>
              <dd className="text-base md:text-lg font-medium text-gray-900">{totalCreditValueRON} RON</dd>
            </div>
          </div>
        </div>
        </div>
      </div>
      {error && (
        <div className="text-center text-red-500 mb-4">{error}</div>
      )}
      {/* Cards on mobile, table on desktop */}
      <div className="block lg:hidden">
        <div className="grid gap-6 md:grid-cols-2">
          {drivers.map(driver => (
            <div key={driver.id} className="bg-white rounded-xl shadow-md p-6 flex flex-col gap-2 border border-slate-200 hover:shadow-lg transition-all">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-gradient-to-br from-purple-500 to-blue-600 rounded-full p-3">
                  <UserIcon className="text-white h-6 w-6" />
                </div>
                <div>
                  <div className="font-semibold text-lg text-slate-800">{driver.name}</div>
                  <div className="text-xs text-slate-400">ID: {driver.id}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-600"><EnvelopeIcon className="text-purple-400 h-5 w-5" /> {driver.email}</div>
              <div className="flex items-center gap-2 text-slate-600"><PhoneIcon className="text-purple-400 h-5 w-5" /> {driver.phone}</div>
              <div className="flex items-center gap-2 text-slate-600 font-semibold mt-2">Cars Registered: {driver.cars.length}</div>
              <div className="flex flex-col gap-1 ml-2">
                {driver.cars.map((car, i) => (
                  <div key={i} className="bg-slate-50 rounded p-2 border border-slate-100 text-xs flex flex-col gap-1">
                    <div><span className="font-semibold">{car.make} {car.model}</span> ({car.registration})</div>
                    <div className="flex flex-wrap gap-2">
                      <span>ITP: <span className="font-mono">{car.itp}</span></span>
                      <span>RCA: <span className="font-mono">{car.rca}</span></span>
                      <span>CASCO: <span className="font-mono">{car.casco || '-'}</span></span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mt-2 text-sm">
                <span className="bg-purple-100 text-purple-700 rounded px-2 py-1">Appointments: {driver.appointments}</span>
                <span className="bg-blue-100 text-blue-700 rounded px-2 py-1">VIN Checks: {driver.vinchecks}</span>
                <span className="bg-green-100 text-green-700 rounded px-2 py-1">Credits: {driver.credits}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="hidden lg:block">
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Drivers</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              All registered drivers with car and activity details
            </p>
          </div>
          {drivers.length === 0 ? (
            <div className="p-6 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No drivers</h3>
              <p className="mt-1 text-sm text-gray-500">No drivers found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cars</th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Car Details</th> */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appointments</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VIN Checks</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {drivers.map(driver => (
                    <tr key={driver.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{driver.name}</div>
                        <div className="text-xs text-gray-400">ID: {driver.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{driver.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{driver.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                          {driver.cars.length}
                        </span>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          {driver.cars.map((car, i) => (
                            <div key={i} className="mb-1">
                              <span className="font-semibold">{car.make} {car.model}</span> <span className="text-xs text-gray-500">({car.registration})</span><br />
                              <span className="text-xs text-gray-500">ITP: <span className="font-mono">{car.itp}</span> | RCA: <span className="font-mono">{car.rca}</span> | CASCO: <span className="font-mono">{car.casco || '-'}</span></span>
                            </div>
                          ))}
                        </div>
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                          {driver.appointments}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                          {driver.vinchecks}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                          {driver.credits}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {!loading && drivers.length === 0 && !error && (
        <div className="text-center text-slate-400 mt-10">No drivers found.</div>
      )}
    </div>
  );
};

export default Drivers;
